<script setup lang="ts">
// icons
import { faTimesCircle, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';

/********************
 * PROPS & EMITS    *
 ********************/
export interface CiLocationInputProps {
  isSubmit?: boolean;
  searchString?: string;
}
const props = withDefaults(defineProps<CiLocationInputProps>(), {
  isSubmit: false,
});

const emit = defineEmits<{
  change: [value: string];
  clear: [value?: any[] | undefined];
  focus: [];
  submit: [];
}>();

defineExpose({
  setFocus,
});

/********************
 * COMPOSITIONS      *
 ********************/
const { $gettext } = useGettext();

/********************
 * REFS & VARS       *
 ********************/
const locationInputRef = ref<HTMLInputElement | null>(null);

const i18nPlaceholderLocation = computed(() => {
  return $gettext('Country, region, campsite');
});

/********************
 * FUNCTIONS         *
 ********************/
function onClear() {
  setFocus();
  emit('clear', [{ q: '' }]);
}

function setFocus() {
  locationInputRef.value?.focus();
}
</script>

<template>
  <div
    class="flex h-[66px] w-full flex-col rounded-[2rem] border border-gray-40 shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
    @click="setFocus"
  >
    <div class="relative flex h-full items-center p-2">
      <!-- input -->
      <input
        id="location"
        ref="locationInputRef"
        tabindex="0"
        :placeholder="i18nPlaceholderLocation"
        :value="searchString"
        class="search__input input-field truncate !px-4 focus:outline-none"
        type="text"
        data-testid="ci-location-input--input"
        autocomplete="off"
        autofocus
        data-1p-ignore
        @focus="emit('focus')"
        @keydown.esc.prevent="onClear"
        @keydown.enter.prevent="emit('submit')"
        @input="emit('change', ($event.target as HTMLInputElement)?.value)"
      />

      <!-- cta -->
      <div class="flex items-center">
        <!-- clear -->
        <CiButton
          v-if="searchString !== ''"
          tag="button"
          variant="text"
          class="mr-1 opacity-40 transition-opacity duration-200 ease-out hover:opacity-60"
          @click.stop="onClear"
        >
          <CiAwesomeIcon
            :icon="faTimesCircle"
            ratio="0.7"
          />
          <span class="hidden">Clear Search</span>
        </CiButton>

        <!-- submit -->
        <CiButton
          tag="button"
          :variant="props.isSubmit ? 'warning' : 'primary'"
          class="max-w-none"
          size="icon"
          data-testid="ci-location-input--submit"
          @click.stop="emit('submit')"
        >
          <span class="sr-only">{{ $pgettext('Search button text', 'Search') }}</span>
          <CiAwesomeIcon
            v-if="!props.isSubmit"
            :icon="faChevronRight"
            :ratio="'0.6'"
          />
          <CiAwesomeIcon
            v-else
            :icon="faSearch"
            :ratio="'0.6'"
          />
        </CiButton>
      </div>
    </div>
  </div>
</template>
